import { QueueingSubject } from 'queueing-subject';
import { Observable, Subject, throwError, timer, filter, map, mergeMap, retryWhen, share, tap } from 'rxjs';

import { AuthService } from '@personal-area/global/authorization/services';

import { WebsocketConnection } from './websocket.connection';
import { WebsocketFallbackService } from './websocket-fallback.service';
import { SocketConnectionState, SocketConnectionStatus } from '../models/socket';

export interface WebsocketOutput {
  output<T>(): Observable<T>;
}

const defaultReconnectInterval = 1000;
const maxRetryAttempts = 10;

export class WebsocketClient implements WebsocketOutput {

  private _output: Observable<any>;
  private _errors: Subject<any>;
  private _filters: Record<string, any>;
  private readonly _options: string[];

  private _isConnected: boolean;
  private _isCreated: boolean;

  constructor(
    private readonly _connection: WebsocketConnection,
    private readonly _authService: AuthService,
    private readonly _wsFallbackService: WebsocketFallbackService
  ) {
    this._options = [];
    this._errors = new Subject<any>();
    this._isConnected = false;
  }

  public get errors(): Observable<any> {
    return this._errors.asObservable();
  }

  public get connectionStatus(): Observable<SocketConnectionStatus> {
    return this._connection.connectionStatus.pipe(
      map(status => {
        return {
          isConnected: status.numberConnected > 0,
          isCreated: status.isCreated,
          isPending: status.isPending
        }
      }),
    );
  }

  public get isCreated(): boolean {
    return this._isCreated;
  }

  public init(reset = false): void {
    if (this.isCreated && !reset) {
      return;
    }
    const input = new QueueingSubject<object>();

    if (this._filters) {
      input.next(this._filters);
    }

    this._isConnected = false;

    this._output = this._connection.connect(this._options, this._filters, reset).pipe(
      tap(data => {
        if (data.error) {
          throw data;
        }
      }),
      retryWhen(errors => errors.pipe(
        filter(() => this._authService.isLoggedIn),
        mergeMap((error, i) => {
          this._reconnect();
          const retryAttempt = i + 1;
          if (retryAttempt > maxRetryAttempts) {
            this._errors.next(error);
            return throwError(error);
          }
          return timer(defaultReconnectInterval);
        }),
      ))
    ).pipe(share());

    this.errors.subscribe();

    this.connectionStatus.subscribe(status => {
      this._isConnected = status.isConnected;
      this._wsFallbackService.socketConnectionState
        .next(status.isPending ?
          SocketConnectionState.initialization :
          status.isConnected ?
            SocketConnectionState.connected :
            SocketConnectionState.failed);
      this._isCreated = status.isCreated;
    });
  }

  private _reconnect(): void {
    if (this._options.indexOf('Bearer') !== -1) {
      this.useAuth();
    }
    this.init(true);
  }

  public useAuth(): WebsocketClient {
    const i = this._options.indexOf('Bearer');
    if (i === -1) {
      this._options.push(
        'Bearer',
        this._authService.isLoggedIn ? this._authService.authToken?.accessToken ?? '' : '',
      );
    } else {
      this._options[i + 1] = this._authService.isLoggedIn ? this._authService.authToken?.accessToken ?? '' : '';
    }
    return this;
  }

  public useFilters(filters: Record<string, any>): WebsocketClient {
    this._filters = filters;
    return this;
  }

  public output<T>(): Observable<T> {
    return this._output;
  }
}
